import React, {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import "./Header.css";
import {Box, Link, Stack, Toolbar} from "@mui/material";
import {useKeycloak} from "@react-keycloak/web";
import {ProvidersService} from "../services/ProvidersService";
import {UserService} from "../services/UserService";
import {useIsProvider} from "../hooks/useIsProvider";
import { Avatar, Menu, MenuItem, ListItemIcon, Divider, IconButton, Tooltip} from "@mui/material";
import {ManageAccounts, Logout, Login, LocalOffer, CalendarMonth, History, AccountBalance} from "@mui/icons-material";
import SearchBar from "./SearchBar";
import UseWindowDimensions from "./GetWindowDimensions";
import {Constants} from "../Constants";
import { getConfig } from '../configService';

function Header() {
    const navigate = useNavigate();
    const {keycloak} = useKeycloak();
    const {width} = UseWindowDimensions();
    const isProvider = useIsProvider();
    const [profilePicture, setProfilePicture] = useState(null);
    const isHomePage = window.location.pathname==="/";
    const config = getConfig();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const authenticated = keycloak.authenticated;
    useEffect( () => {
        if (authenticated) {
            // Get user profile to load profile picture
            getUserProfile();
        }
    }, [authenticated]);

    useEffect(() => {
        // Add event listener to update profile picture when a custom event is fired
        window.addEventListener('profileUpdated', getUserProfile);

        return () => {
            window.removeEventListener('profileUpdated', getUserProfile);
        };
    }, []);

    const getUserProfile = async () => {
        if (authenticated) {
            try {
                const response = await UserService.getUserProfile();
                if (response && response.status === 200 && response.data.thumbnail != null && response.data.thumbnail.length > 0) {
                    let src= config.imageBaseUrl + response.data.id + "/" + response.data.thumbnail + ".jpg"
                    setProfilePicture(src);
                } else {
                    setProfilePicture(null);
                }
            } catch (error) {
                console.error('Error loading user profile in header:', error);
                setProfilePicture(null);
            }
        }
    };

    document.addEventListener("scroll", ()=>{handleClose()})

    return (
        <Box className="headerBackground" position={"sticky"} top={"0"} zIndex={1} width={"calc(100vw - 20px)"} >
            <Box sx={{display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 2, padding: '10px'}}>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    {width>Constants.MOBILE_WIDTH ? (
                        <img data-testid='pairselfcarelogo' className="navLogo" src={require('../../src/assets/svgs/PairCareLogoBlue.png')} alt={"Home"} height={'64px'} onClick={()=>{navigate("/")}} style={{cursor: 'pointer'}} />
                    ) : (
                        <img data-testid='pairselfcarelogo' className="navLogo" src={require('../../src/assets/svgs/PairSelfCareIconBlue.png')} alt={"Home"} height={'64px'} onClick={()=>{navigate("/")}} style={{cursor: 'pointer'}} />
                    )}
                </Box>

                <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                    {isHomePage && <Box sx={{width: '100%', maxWidth: '518px'}}><SearchBar /></Box>}
                </Box>

                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                    <Box >
                        {(!isProvider && width>Constants.MOBILE_WIDTH) &&
                            <Link className="linkItem" onClick={() => {
                                handleClose();
                                navigate("/registration", { replace: false});
                            }}>List Your Business</Link>}
                    </Box>

                    <Tooltip title="Account settings">
                        <IconButton
                            onClick={handleClick}
                            size="small"
                            
                            aria-controls={open ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                        >
                          {authenticated && profilePicture ? (
                            <Avatar
                              src={profilePicture}
                              alt={`${keycloak.tokenParsed.given_name} ${keycloak.tokenParsed.family_name}`}
                            />
                          ) : (
                            authenticated ? (
                              <Avatar>{keycloak.tokenParsed.given_name[0].toUpperCase()+keycloak.tokenParsed.family_name[0].toUpperCase()}</Avatar>
                            ) : (
                              <Avatar />
                            )
                          )}
                        </IconButton>
                    </Tooltip>

                    {/* account menu */}
                    <Menu
                        anchorEl={anchorEl}
                        id="account-menu"
                        open={open}
                        disableScrollLock={true}
                        onClose={handleClose}
                        onClick={handleClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                padding: 0,
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                        {authenticated && <MenuItem onClick={() => {
                            handleClose();
                            navigate("/userprofile");
                        }}>
                            <ListItemIcon>
                                <ManageAccounts fontSize="small"/>
                            </ListItemIcon>
                            Account
                        </MenuItem>}

                        {authenticated && <MenuItem onClick={() => {
                            handleClose();
                            navigate("/appointments");
                        }}>
                            <ListItemIcon>
                                <CalendarMonth fontSize="small"/>
                            </ListItemIcon>
                            Appointments
                        </MenuItem>}

                        {authenticated && <MenuItem onClick={() => {
                            handleClose();
                            navigate("/purchaseHistory");
                        }}>
                            <ListItemIcon>
                                <History fontSize="small"/>
                            </ListItemIcon>
                            Purchase History
                        </MenuItem>}

                        {authenticated && <Divider/>}

                        {authenticated && isProvider && <MenuItem onClick={() => {
                            handleClose();
                            navigate("/profile");
                        }}>
                            <ListItemIcon>
                                <AccountBalance fontSize="small"/>
                            </ListItemIcon>
                            Business Profile
                        </MenuItem>}

                        {!isProvider && <MenuItem onClick={() => {
                            handleClose();
                            navigate("/registration", {replace: false});
                        }}>
                            <ListItemIcon>
                                <LocalOffer fontSize="small"/>
                            </ListItemIcon>
                            List Your Business
                        </MenuItem>}


                        <Divider/>

                        {authenticated && <MenuItem onClick={() => {
                            handleClose();
                            navigate("/");
                            keycloak.logout();
                        }}>
                            <ListItemIcon>
                                <Logout fontSize="small"/>
                            </ListItemIcon>
                            Logout
                        </MenuItem>}
                        {!authenticated && <MenuItem onClick={() => {
                            keycloak.login();
                            handleClose();
                        }}>
                            <ListItemIcon>
                                <Login fontSize="small"/>
                            </ListItemIcon>
                            Login
                        </MenuItem>
                        }

                    </Menu>
                </Box>
            </Box>
        </Box>
    );
}

export default Header;